import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import Breadcrumb from 'components/breadcrumb'
import { verifyLogin } from 'util/cache'
import { useNavigate } from 'react-router-dom'
import styles from './lesson-overview-page.module.css'
import { GradesDataProvider, useGradesData, StudentRow } from './lesson-overview-context'
import { GradesTable, ViewMode } from './lesson-overview-table'
import { average, hardestSlot, spreadPoints, mostCommonWrongAnswer, SlotPercent } from 'util/grades-grid'

const TIERS = ['Emerging', 'Intermediate', 'Advanced'] as const

const scoreFor = (rows: StudentRow[]) => rows.map(r => r.overallPct).filter((v): v is number => v !== null)

const AlertBanner = ({ tierAverages, hardest, hardestPct, list }: {
	tierAverages: number[]
	hardest: SlotPercent | null
	hardestPct: number | null
	list: StudentRow[]
}) => {
	const spread = spreadPoints(tierAverages)

	if (tierAverages.length >= 2 && spread > 15) {
		return (
			<div className={`${styles.alert} ${styles.alertWarn}`}>
				<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12.01" y2="16.5" /></svg>
				<span><strong>{spread} points</strong> between the highest and lowest level — one group may have questions pitched wrong for them.</span>
			</div>
		)
	}

	if (hardest && hardestPct !== null && hardestPct < 60) {
		const wrong = mostCommonWrongAnswer(
			list.flatMap(r => {
				const cell = r.cells[hardest.slotIndex]
				return cell && (cell.state === 'correct' || cell.state === 'incorrect')
					? [{ answer: cell.studentAnswer || '', correct: cell.state === 'correct' }]
					: []
			})
		)
		return (
			<div className={`${styles.alert} ${styles.alertWarn}`}>
				<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12.01" y2="16.5" /></svg>
				<span>Only <strong>{hardestPct}%</strong> got Q{hardest.slotIndex + 1} right{
					wrong && wrong.n > 1
						? <> — {wrong.n} of {wrong.wrongTotal} answered <strong>{wrong.text}</strong>.</>
						: '. Worth revisiting.'
				}</span>
			</div>
		)
	}

	if (tierAverages.length >= 2) {
		return (
			<div className={`${styles.alert} ${styles.alertInfo}`}>
				<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
				<span>The levels are within {spread} points of each other — the questions look well pitched.</span>
			</div>
		)
	}

	return null
}

const GradesContent = () => {
	const { classId } = useParams()
	const navigate = useNavigate()
	const { lessonName, isLoading, error, slots, studentRows } = useGradesData()
	const [tierFilter, setTierFilter] = useState<string>('All')
	const [view, setView] = useState<ViewMode>('grid')

	React.useEffect(() => { verifyLogin(navigate) }, [navigate])

	const visibleRows = useMemo(
		() => tierFilter === 'All' ? studentRows : studentRows.filter(r => r.tier === tierFilter),
		[studentRows, tierFilter]
	)

	const slotPercents: SlotPercent[] = useMemo(() => slots.map((_, slotIndex) => {
		const graded = visibleRows
			.map(r => r.cells[slotIndex])
			.filter(c => c.state === 'correct' || c.state === 'incorrect')
		return { slotIndex, pct: graded.length ? Math.round(graded.filter(c => c.state === 'correct').length / graded.length * 100) : null }
	}), [slots, visibleRows])

	const classAverage = average(scoreFor(visibleRows)) ?? 0
	const hardest = hardestSlot(slotPercents)
	const notSubmitted = visibleRows.reduce((n, r) => n + r.cells.filter(c => c.state === 'not-submitted').length, 0)

	const tierAverages = TIERS
		.map(t => average(scoreFor(studentRows.filter(r => r.tier === t))))
		.filter((v): v is number => v !== null)

	if (error) {
		return (
			<div className={styles.page}>
			<div className={styles.container}>
				<h1 className={styles.title}>We couldn't load grades</h1>
				<p>Check your connection, then <a href="#retry" onClick={e => { e.preventDefault(); window.location.reload() }}>try again</a>.</p>
			</div>
			</div>
		)
	}

	if (isLoading) {
		return <div className={styles.page}><div className={styles.container}><h1 className={styles.title}>Loading...</h1></div></div>
	}

	return (
		<div className={styles.page}>
		<div className={styles.container}>
			<Breadcrumb items={[
				{ label: 'My Lesson Library', path: '/lessons' },
				{ label: 'Classroom', path: `/lessons?classId=${classId}` },
				{ label: lessonName || 'Lesson' },
			]} />

			<h1 className={styles.title}>Grades</h1>
			<p className={styles.sub}>{lessonName} · {slots.length} question{slots.length === 1 ? '' : 's'} · {studentRows.length} student{studentRows.length === 1 ? '' : 's'}</p>

			<div className={styles.statband}>
				<div className={styles.sbItem}>
					<span className={styles.sbK}>Class average</span>
					<span className={styles.sbV}>{classAverage}<small>%</small></span>
				</div>
				<div className={styles.sbItem}>
					<span className={styles.sbK}>Students</span>
					<span className={styles.sbV}>{visibleRows.length}</span>
				</div>
				{hardest && hardest.pct !== null && (
					<div className={styles.sbItem}>
						<span className={styles.sbK}>Hardest</span>
						<span className={styles.sbV}>Q{hardest.slotIndex + 1}<small>{hardest.pct}%</small></span>
					</div>
				)}
				<div className={styles.sbDiv} />
				<div className={styles.sbTiers}>
					{TIERS.map(tier => {
						const rows = studentRows.filter(r => r.tier === tier)
						const pct = average(scoreFor(rows))
						return (
							<div key={tier} className={`${styles.sbTier} ${styles['sbt' + tier]}`}>
								<div className={styles.sbTierRow}><span className={styles.sbTierName}>{tier}</span><span className={styles.sbTierPct}>{pct === null ? '—' : `${pct}%`}</span></div>
								<div className={styles.sbTrack}><div className={styles.sbFill} style={{ width: `${pct || 0}%` }} /></div>
								<div className={styles.sbN}>{rows.length}</div>
							</div>
						)
					})}
				</div>
				{notSubmitted > 0 && (
					<div className={styles.sbCta}>
						<span className={styles.sbOutstanding}>{notSubmitted} not yet submitted</span>
					</div>
				)}
			</div>

			<AlertBanner tierAverages={tierAverages} hardest={hardest} hardestPct={hardest?.pct ?? null} list={visibleRows} />

			<div className={styles.controls}>
				<div className={styles.tierTabs} role="tablist" aria-label="Filter by tier">
					{['All', ...TIERS].map(tier => (
						<button
							key={tier}
							type="button"
							role="tab"
							aria-selected={tierFilter === tier}
							className={`${styles.tierTab} ${tierFilter === tier ? styles.tierTabActive : ''}`}
							onClick={() => setTierFilter(tier)}
						>
							{tier} ({tier === 'All' ? studentRows.length : studentRows.filter(r => r.tier === tier).length})
						</button>
					))}
				</div>
				<div className={styles.viewToggle} role="group" aria-label="View">
					<button type="button" className={`${styles.viewBtn} ${view === 'grid' ? styles.viewBtnActive : ''}`} onClick={() => setView('grid')}>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
						Grid
					</button>
					<button type="button" className={`${styles.viewBtn} ${view === 'list' ? styles.viewBtnActive : ''}`} onClick={() => setView('list')}>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
						List
					</button>
				</div>
			</div>

			<GradesTable view={view} rows={visibleRows} slotPercents={slotPercents} />
		</div>
		</div>
	)
}

export const LessonOverviewPage = () => {
	const { classId, lessonId } = useParams()
	if (!classId || !lessonId) return null
	return (
		<GradesDataProvider classId={classId} lessonId={lessonId}>
			<GradesContent />
		</GradesDataProvider>
	)
}

export default LessonOverviewPage
